// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { EmailCampaignGridModel } from '../../../state_manager/data_models/email_campaign_grid_model.ts'
import { FilterValue, SortConfig } from '../../../state_manager/interfaces/email.ts'
import { fetchEmailCampaignsFiltered } from '../services/emailService.ts'

export const DEFAULT_CAMPAIGNS_SORTING: SortConfig[] = [
  { key: 'email_campaigns__date_modified', direction: 'desc' }
]

export interface UseEmailCampaignsArgs {
  // Tab-specific initial state. Drafts tab uses default sort + status=['draft'];
  // Sent log tab uses dispatched_at desc + status-not-draft. Defaults keep
  // existing callers (Phase 2-style "all campaigns") working as before.
  initialSorting?: SortConfig[]
  initialFilters?: FilterValue[]
}

/**
 * useEmailCampaigns — Phase 2 list grid hook, extended Phase 5 to host
 * the Drafts and Sent log tabs.
 *
 * Mirrors useDistributionLists: its own data RPC (view__email_campaigns),
 * screen-local state (no Redux pre-fetch). Each tab instantiates its own
 * copy with tab-specific initial sort + filters; the hook then owns
 * filter/sort/page mutation from there.
 */
export const useEmailCampaigns = ({
  initialSorting = DEFAULT_CAMPAIGNS_SORTING,
  initialFilters = []
}: UseEmailCampaignsArgs = {}) => {
  const [campaignsData, setCampaignsData] = useState<EmailCampaignGridModel[]>([])
  const [campaignsLoading, setCampaignsLoading] = useState(false)
  const [activeSorting, setActiveSorting] = useState<SortConfig[]>(initialSorting)
  const [activeFilters, setActiveFilters] = useState<FilterValue[]>(initialFilters)

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const handleFetchCampaigns = useCallback(
    async (sorting: SortConfig[] = DEFAULT_CAMPAIGNS_SORTING, filters: FilterValue[] = []) => {
      try {
        setCampaignsLoading(true)
        const token = getAccessToken(store.getState().auth)
        const response = await fetchEmailCampaignsFiltered(
          sorting && sorting.length > 0 ? sorting : DEFAULT_CAMPAIGNS_SORTING,
          filters ?? [],
          1,
          null,
          token
        )
        if (cancelledRef.current) return
        if (response.error) {
          if (process.env.NODE_ENV === 'development') console.error(response.error)
        }
        setCampaignsData(response.data)
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error(e)
      } finally {
        if (!cancelledRef.current) setCampaignsLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    const token = getAccessToken(store.getState().auth)
    if (token) handleFetchCampaigns(activeSorting, activeFilters)
  }, [activeFilters, activeSorting, handleFetchCampaigns])

  const handleSetSorting = useCallback((sorting: SortConfig[]) => {
    setActiveSorting(sorting)
  }, [])

  const handleSetFilters = useCallback((filters: FilterValue[]) => {
    setActiveFilters(filters)
  }, [])

  const refetch = useCallback(
    () => handleFetchCampaigns(activeSorting, activeFilters),
    [handleFetchCampaigns, activeSorting, activeFilters]
  )

  return {
    campaignsData,
    campaignsLoading,
    activeSorting,
    activeFilters,
    handleSetSorting,
    handleSetFilters,
    refetch
  }
}
